<template>
  <v-app class="back" :class="{ dark: isDarkMode }">
    <template v-if="!$vuetify.breakpoint.smAndDown">
      <!-- side nav -->

      <v-navigation-drawer class="wfm-navbar z-30" v-model="drawer" :mini-variant="miniVariant" fixed
        :color="$vuetify.theme.currentTheme.navigationColor" app permanent :expand-on-hover="false" width="270"
        mini-variant-width="120">
        <div class="my-2 font-bold">
          <div class="sidebar-brand-wrapper py-3 px-3">
            <div class="flex justify-center items-center">
              <div class="image-container relative flex items-center justify-center">
                <template v-if="orgData">
                  <div class="flex items-center justify-center">
                    <img class="sidebar-logo cursor-pointer" v-if="orgData.logo" :src="getLogoImage"
                      @click="goToDashboard" />
                    <img v-else class="sidebar-logo cursor-pointer" src="@/assets/img/CW_DEFAULT.png"
                      @click="goToDashboard" />
                  </div>
                </template>
                <template v-else>
                  <v-skeleton-loader height="36" type="card" width="80"></v-skeleton-loader>
                </template>
              </div>
            </div>
          </div>

          <div class="sidebar-divider"></div>

          <v-list class="scrollable-list py-2" dense>
            <template v-for="group in groupedMenuItems">
              <div :key="group.key" class="sidebar-category-group">
                <!-- Category Header (only when expanded) -->
                <div v-if="!miniVariant" class="sidebar-category-header">
                  {{ group.title }}
                </div>

                <!-- Category Items -->
                <template v-for="item in group.items">
                  <!-- Direct route -->
                  <v-list-item v-if="item.to && (!item.childRoutes || item.childRoutes.length === 0)"
                    :key="(item.id || '') + '-' + (item.to || item.originalIndex)" :to="item.to" router exact :class="[
                      'sidebar-nav-item',
                      isItemSelected(item) ? 'sidebar-pill-active' : 'sidebar-pill-inactive'
                    ]" @mouseenter="showPopup(item.originalIndex, $event)" @mouseleave="scheduleHidePopup">
                    <div class="sidebar-item-row flex items-center w-full">
                      <v-icon size="20" class="sidebar-item-icon mr-2.5 flex-shrink-0"
                        :color="isItemSelected(item) ? '#0B2353' : '#cbd7e8'">
                        {{ item.icon }}
                      </v-icon>
                      <span class="sidebar-item-title text-sm truncate"
                        :class="isItemSelected(item) ? 'font-bold text-[#0B2353]' : 'text-[#cbd7e8]'">
                        {{ item.title }}
                      </span>
                      <v-spacer v-if="!miniVariant"></v-spacer>
                      <span v-if="!miniVariant && getItemBadge(item)" class="sidebar-badge ml-2 flex-shrink-0"
                        :class="isItemSelected(item) ? 'sidebar-badge-active' : 'sidebar-badge-inactive'">
                        {{ getItemBadge(item) }}
                      </span>
                    </div>
                  </v-list-item>

                  <!-- Nested Group (Expandable) -->
                  <v-list-group v-else-if="item.childRoutes && item.childRoutes.length > 0"
                    :key="'group-' + (item.id || item.originalIndex)" :value="isItemSelected(item)" :class="[
                      'sidebar-nav-group',
                      isItemSelected(item) ? 'group-active' : ''
                    ]" @mouseenter.native="showPopup(item.originalIndex, $event)"
                    @mouseleave.native="scheduleHidePopup" append-icon="mdi-chevron-down">
                    <template v-slot:activator>
                      <div class="sidebar-item-row flex items-center w-full">
                        <v-icon size="20" class="sidebar-item-icon mr-2.5 flex-shrink-0"
                          :color="isItemSelected(item) ? '#0B2353' : '#cbd7e8'">
                          {{ item.icon }}
                        </v-icon>
                        <span class="sidebar-item-title text-sm truncate"
                          :class="isItemSelected(item) ? 'font-bold text-[#0B2353]' : 'text-[#cbd7e8]'">
                          {{ item.title }}
                        </span>
                        <v-spacer v-if="!miniVariant"></v-spacer>
                        <span v-if="!miniVariant && getItemBadge(item)" class="sidebar-badge ml-2 flex-shrink-0"
                          :class="isItemSelected(item) ? 'sidebar-badge-active' : 'sidebar-badge-inactive'">
                          {{ getItemBadge(item) }}
                        </span>
                      </div>
                    </template>

                    <!-- Nested child items (when expanded) -->
                    <template v-if="!miniVariant">
                      <v-list-item v-for="(child, j) in item.childRoutes" :key="child.id || j" :to="child.to" router
                        exact :class="[
                          'sidebar-child-item',
                          isChildActive(child) ? 'sidebar-child-active' : 'sidebar-child-inactive'
                        ]">
                        <v-icon size="16" class="ml-4 mr-3" :color="isChildActive(child) ? '#ffffff' : '#a2b9d9'">
                          {{ child.icon || 'mdi-circle-small' }}
                        </v-icon>
                        <v-list-item-content class="py-1">
                          <v-list-item-title class="text-xs"
                            :class="isChildActive(child) ? 'font-bold text-white' : 'text-[#cbd7e8]'">
                            {{ child.title }}
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-list-group>
                </template>
              </div>
            </template>
          </v-list>
        </div>
      </v-navigation-drawer>
      <!-- main header -->
      <v-app-bar fixed dense class="flex justify-between wfm-top-navbar"
        :color="$vuetify.theme.currentTheme.appBarColor" elevation="0" app>
        <div class="d-flex justify-center">
          <v-app-bar-nav-icon @click.stop="miniVariant = !miniVariant" class="ma-0" color="white"></v-app-bar-nav-icon>
          <div class="flex-2 flex justify-center items-center ml-4" v-if="showGlobalSearch">
            <GlobalSearch :enableFetch="allowEnableFetch" class="w-full max-w-xl" />
          </div>
        </div>

        <div class="flex justify-end items-center shrink-0">
          <!-- Send Message (Notification) -->
          <v-btn color="white" outlined small class="font-bold normal-case rounded-lg mr-4 px-4"
            @click="showNotificationDialog = true" v-iam-disabled="`global.notifications.write`">
            <v-icon left small>mdi-email-outline</v-icon>
            Send Message
          </v-btn>

          <div class="notification mr-4">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on" v-show="isbellIconEnabled('NOTIFICATIONS')">
                  <v-badge color="red" overlap :content="alertsCount" v-if="alertsCount">
                    <v-icon class="ml-2 white--text" @click="toggleDropdown">mdi-bell</v-icon>
                  </v-badge>
                  <v-icon class="ml-2 white--text" @click="toggleDropdown" v-else>mdi-bell</v-icon>
                </div>
              </template>

              <v-card class="w-[28rem] max-h-[40rem]">
                <v-list class="mt-4 w-full">
                  <v-list-item v-for="(notification, index) in alerts.slice(0, 4)" :key="index">
                    <v-alert :type="notification.type" class="w-full p-1 text-base"
                      :color="categoryColor(notification.notificationGenric)?.bgColor" text outlined>
                      {{ notification.message }}
                    </v-alert>
                  </v-list-item>
                  <v-list-item v-if="!alerts.length">
                    <v-alert type="warning" color="blue" class="w-full p-1 text-base">
                      No alerts
                    </v-alert>
                  </v-list-item>
                  <v-list-item @click="handleSeeAll"
                    class="cursor-pointer flex justify-center hover:bg-slate-200 hover:dark-icon-bg dark-text-color mx-4 rounded">
                    See all &nbsp;
                    <span v-if="this.alertsCount > 4"> (+{{ this.alertsCount - 4 }} more)</span>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>

          <div class="user-menu relative z-30">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="custom-btn">
                  <div>
                    <img v-if="user?.user?.profile_image" :src="getEmployeeImage()" alt="Employee Image"
                      class="border border-white-500 rounded-full h-7 w-7 inline -mt-1 mr-2" />
                    <v-icon v-else class="border border-white-500 rounded-full white--text mr-2">mdi-account</v-icon>
                    <span class="white--text capitalize">Hi, {{ user_name }} </span>
                  </div>
                </v-btn>
              </template>
              <v-list>
                <v-list-item to="/workforce/profile">Profile</v-list-item>
                <!-- <v-list-item to="/workforce/org">Company Settings</v-list-item> -->
                <v-list-item @click="logout()" class="border-b">Logout</v-list-item>
                <v-list-item @click.prevent="openInNewTab"><span class="colorText">About Product</span></v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-app-bar>

      <!-- fixed bottom navbar -->
      <v-footer app inset padless fixed height="50" class="wfm-bottom-navbar px-4" elevation="0">
        <div class="wfm-footer-content">
          <!-- Copyright Notice -->
          <div class="wfm-footer-copyright">
            © {{ currentYear }} CualityWork. All Rights Reserved.
          </div>

          <!-- Right-aligned action buttons -->
          <div class="flex justify-end">
            <!-- Widget Guide -->
            <v-btn small elevation="0" class="font-semibold normal-case rounded-lg mr-2 px-3 bottom-nav-btn"
              @click="openGuideDialog">
              <v-icon left small color="#0B2353">mdi-book-open-page-variant</v-icon>
              Widget Guide
            </v-btn>

            <!-- Help Desk Button -->
            <v-btn small elevation="0"
              class="font-semibold normal-case rounded-lg mr-2 px-3 bottom-nav-btn help-desk-trigger"
              @click="openComplaintCard" title="Help Desk">
              <v-icon left small color="#0B2353">mdi-comment-processing-outline</v-icon>
              Help Desk
            </v-btn>

            <!-- Watch Tutorial -->
            <v-btn small elevation="0" class="font-semibold normal-case rounded-lg mr-3 px-3 bottom-nav-btn"
              @click="openTutorialDialog">
              <v-icon left small color="#0B2353">mdi-play-circle-outline</v-icon>
              Watch Tutorial
            </v-btn>
          </div>
          <!-- Google Play (beside watch tutorial, right aligned) -->
          <div>
            <img src="@/assets/img/Google_Play.png" alt="Download App" class="cursor-pointer download-image"
              @click="downloadApp" />
          </div>

        </div>
      </v-footer>
    </template>

    <!-- Mobile Navigation Elements -->
    <DefaultLayout_mobile v-else />

    <!-- main content -->
    <v-main :class="{ 'mobile-main-content': $vuetify.breakpoint.smAndDown }">
      <div class="flex gap-2 my-1" v-if="isDialerOpen">
        <div class="dialer-overlay">
          <div class="dialer-container">
            <DialerPopOver @handleCloseDialer="openDialerCard" />
          </div>
        </div>
      </div>
      <div class="px-2">
        <MobileBlocker v-if="$vuetify.breakpoint.smAndDown && !isRouteAllowedOnMobile" />
        <div v-else-if="!isRouteAllowed" class="pa-10 text-center grey--text">
          <v-icon size="64" color="grey lighten-2">mdi-lock-outline</v-icon>
          <div class="text-subtitle-1 font-weight-bold uppercase mt-4">Access Denied</div>
          <div class="text-caption">You do not have permission to view this page.</div>
        </div>
        <router-view v-else />
      </div>
    </v-main>
    <v-dialog v-model="showUpgradePlan" max-width="1200px">
      <v-card class="bg-gray-100 p-4">
        <v-card-title class="headline flex justify-between items-center bg-white px-6 py-4">
          <span class="font-bold text-gray-800">Upgrade Your Plan</span>
          <v-btn icon @click="showUpgradePlan = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <PlanSelection :currentPlanName="currentPlanName" :processingPlan="loadingPlan"
            @select-plan="handlePlanSelection" @billing-cycle-change="billingCycle = $event" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <RazorpayCheckout ref="razorpayCheckout" :planUserName="userName" :planUserEmail="userEmail"
      :planUserContact="userContact" :orgInfo="razorpayOrgInfo" :paymentType="billingCycle.toUpperCase()" />

    <div v-if="isRightDrawerOpen" class="flex justify-between">
      <RightDrawer @openCallCutomer="openDialerCard" @openHelpDesk="openComplaintCard" @downloadEmpApp="downloadApp"
        :isCallingAllowed="isCallingAllowed('CALL_A_CUSTOMER')" />
    </div>

    <!-- Help Desk / Query Component Modal -->
    <QueryComponent v-if="isComplaintOpen" @handleCloseComplaint="openComplaintCard" />

    <v-dialog v-model="planStatus" persistent max-width="450">
      <v-card class="text-center pa-6">
        <v-img src="https://cdn-icons-png.flaticon.com/512/6179/6179025.png" alt="Plan Expired"
          class="mx-auto mb-4 bg-white" max-width="120" />
        <v-card-title class="headline ml-16">Your plan has expired</v-card-title>
        <v-card-text>
          Your access to this service has been temporarily suspended due to an expired plan. Please
          contact Cuality-Work admin ( pranavbarthwal@myfinfi.com ) to renew your plan and regain
          full access.
          <br />
          Rest assured, all your data and settings are saved and secured.
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn :color="$vuetify.theme.currentTheme.primary" class="white--text" small @click="logout">Log Out</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Popup for menu items -->
    <div v-if="showItemPopup && miniVariant" class="popup-container"
      :style="{ top: popupPosition.top + 'px', left: popupPosition.left + 'px' }" @mouseenter="cancelHidePopup"
      @mouseleave="scheduleHidePopup">
      <div class="popup-header" v-if="currentHoveredItemTitle">
        <v-icon size="18" color="white" class="mr-2">{{ currentHoveredItemTitle.icon }}</v-icon>
        <span class="popup-title">{{ currentHoveredItemTitle.title }}</span>
      </div>
      <div class="popup-content pt-2 scrollable-popup">
        <div v-for="(popupItem, index) in popupItems" :key="index" class="popup-item"
          :class="{ 'popup-item-selected': isPopupItemSelected(popupItem) }" @click="navigateTo(popupItem.to)">
          <v-icon size="18" class="mr-2" :color="isPopupItemSelected(popupItem) ? '#0B2353' : '#cbd7e8'">{{
            popupItem.icon
            }}</v-icon>
          <span :class="{
            'font-bold text-[#0B2353]': isPopupItemSelected(popupItem),
            'text-[#cbd7e8]': !isPopupItemSelected(popupItem)
          }">{{ popupItem.title }}</span>
        </div>
      </div>
    </div>
    <!-- Chat Agent Widget -->
    <ChatAgentWidget />

    <!-- Manual Notification Dialog -->
    <SendNotificationDialog v-model="showNotificationDialog" />

    <!-- Widget Guide Dialog -->
    <v-dialog v-model="guideDialog" max-width="1000px">
      <WidgetSettingsGuide :showGuide="false" />
    </v-dialog>

    <!-- Tutorial Multi-Video Carousel Dialog -->
    <TutorialCarouselDialog ref="tutorialCarouselDialog" platform="admin" />
  </v-app>
</template>

<script>
import {
  hasPermission,
  convertToTitleCase,
  isModuleVisible,
  isModuleFeatureAllowed
} from '@/utils/common'
import permissionData from '@/utils/permissions'
import { hasIamPermission } from '@/utils/iamEvaluator'
import { reformatNotification } from '@/utils/workforce/utils'
import {
  NOTIFICATION_CHANNEL,
  PUSHER_CLIENT_LIB,
  routeConfig,
  SearchConfig
  // USER_GUIDE_LINK,
} from '@/utils/workforce/constants'
import { notificationColor } from '@/utils/workforce/statusColors'
import DialerPopOver from '@/components/DialerPopOver.vue'
import QueryComponent from '@/components/queryComponent.vue'
import GlobalSearch from '@/components/GlobalSearch.vue'
import RightDrawer from '@/components/RightDrawer'
import Pusher from 'pusher-js'
import { axiosUrl } from '@/utils/workforceApi'
import PlanSelection from '@/components/common/PlanSelection.vue'
import RazorpayCheckout from '@/components/common/RazorpayCheckout.vue'
import ChatAgentWidget from '@/components/ChatAgentWidget.vue'
import SendNotificationDialog from '@/components/Workforce/Notifications/SendNotificationDialog.vue'
import WidgetSettingsGuide from '@/components/Workforce/Dashboard/WidgetSettingsGuide.vue'
import TutorialCarouselDialog from '@/components/Workforce/Tutorial/TutorialCarouselDialog.vue'
import DefaultLayout_mobile from './DefaultLayout_mobile.vue'
import MobileBlocker from '@/components/common/MobileBlocker.vue'
import { isRouteAllowedOnMobile } from '@/utils/workforce/mobileRoutesConfig'

export default {
  name: 'DefaultLayout',
  provide() {
    return {
      layout: this
    }
  },
  data() {
    return {
      orgCode: this.$storage.getUniversal('organization_code'),
      userOrg: this.$storage.getUniversal('user'),
      clipped: true,
      drawer: true,
      fixed: false,
      miniVariant: true,
      right: true,
      user_name: '',
      menuItems: [],
      orgInfo: null,
      currentDate: this.getCurrentDate(),
      searchQuery: '',
      user: {},
      baseUrlS3: process.env.VUE_APP_S3_BASE_URL,
      dropdownVisible: false,
      alerts: [],
      alertsCount: 0,
      useCases: [],
      orgData: null,
      isDialerOpen: false,
      isComplaintOpen: false,
      planStatus: false,
      isDarkMode: false,
      theme: 'light',
      isLoad: false,
      isHelpTextVisible: true,
      allSubCases: [],
      showItemPopup: false,
      popupPosition: { top: 0, left: 0 },
      popupItems: [],
      hidePopupTimeout: null,
      currentHoveredItem: null,
      isRightDrawerOpen: false,
      allowedUseCases: [],
      // Plan Upgrade Data
      showUpgradePlan: false,
      loadingPlan: null,
      billingCycle: 'monthly',
      planName: '', // current plan name
      daysLeft: null,
      lastPaymentAmount: null,
      showNotificationDialog: false,
      guideDialog: false
    }
  },
  components: {
    DialerPopOver,
    QueryComponent,
    GlobalSearch,
    RightDrawer,
    PlanSelection,
    RazorpayCheckout,
    ChatAgentWidget,
    SendNotificationDialog,
    WidgetSettingsGuide,
    TutorialCarouselDialog,
    DefaultLayout_mobile,
    MobileBlocker
  },

  created() {
    if (typeof window !== 'undefined') {
      this.isDarkMode = false
      this.applyTheme()
      this.isLoad = true
    }
  },

  computed: {
    userData() {
      return this.$store.state.wfmPermissions || []
    },
    title() {
      const orgName = this.orgData?.name || ''
      return orgName && `${orgName.charAt(0).toUpperCase()}${orgName.slice(1).toLowerCase()}`
    },
    // New computed properties for Razorpay
    currentPlanName() {
      return this.planName || 'Base' // derived from api later in checkActivePlan
    },
    currentUser() {
      return this.$storage?.getUniversal('user') || {}
    },
    userName() {
      return (
        `${this.currentUser.first_name || ''} ${this.currentUser.last_name || ''}`.trim() || 'User'
      )
    },
    userEmail() {
      return this.currentUser.email || 'user@example.com'
    },
    userContact() {
      return this.currentUser.mobile
    },
    razorpayOrgInfo() {
      const data = this.$storage.getUniversal('orgData')
      return {
        orgId: data?.id,
        planId: data?.plan_id,
        orgName: data?.name
      }
    },
    // existing
    currentHoveredItemTitle() {
      if (this.currentHoveredItem !== null && this.menuItems[this.currentHoveredItem]) {
        return {
          title: this.menuItems[this.currentHoveredItem]?.title,
          icon: this.menuItems[this.currentHoveredItem]?.icon
        }
      }
      return ''
    },
    groupedMenuItems() {
      const categoryOrder = ['WORKSPACE', 'FINANCE', 'INSIGHTS', 'ADMIN']
      const groups = {
        WORKSPACE: { key: 'WORKSPACE', title: 'WORKSPACE', items: [] },
        FINANCE: { key: 'FINANCE', title: 'FINANCE', items: [] },
        INSIGHTS: { key: 'INSIGHTS', title: 'INSIGHTS', items: [] },
        ADMIN: { key: 'ADMIN', title: 'ADMIN', items: [] }
      }

      this.menuItems.forEach((item, index) => {
        if (!item || !item.visible) return

        const category = this.getItemCategory(item)
        const targetGroup = groups[category] || groups.WORKSPACE
        targetGroup.items.push({
          ...item,
          originalIndex: index
        })
      })

      // Sort items within each category
      Object.values(groups).forEach((group) => {
        group.items.sort((a, b) => this.getItemSortWeight(a) - this.getItemSortWeight(b))
      })

      return categoryOrder.map((key) => groups[key]).filter((g) => g.items.length > 0)
    },
    isClipped() {
      return this.clipped
    },
    selectedItemIndex() {
      return this.menuItems.findIndex((item) => {
        if (item.to && this.$route.path.startsWith(item.to)) {
          return true
        }
        return false
      })
    },
    sparePartEnabled() {
      return (
        hasPermission(this.userData, permissionData.inventoryRead) &&
        isModuleVisible('base_management')
      )
    },
    amcEnabled() {
      return (
        hasPermission(this.userData, permissionData.amcRead) && isModuleVisible('base_management')
      )
    },
    isCollectionEnabled() {
      return isModuleVisible('collection_management')
    },
    checkRouteVisibility() {
      return (
        this.$route.path == '/workforce/leadspage/inventory' &&
        !hasPermission(this.userData, permissionData.leadRead) &&
        this.$route.query?.complaint_no
      )
    },
    iamStatements() {
      return this.$store.state.wfmStatements || []
    },
    isRouteAllowed() {
      // Access meta.permission directly from the matched route descriptors
      const permission = this.$route.meta?.permission
      if (permission) {
        return hasIamPermission(this.iamStatements, permission)
      }
      return true
    },
    isRouteAllowedOnMobile() {
      return isRouteAllowedOnMobile(this.$route.path)
    },
    getLogoImage() {
      if (this.orgData?.logo) {
        return `${process.env.VUE_APP_S3_BASE_URL + this.orgData?.logo}`
      }
      return null
    },
    isEveryTabsInModule() {
      return (
        isModuleFeatureAllowed('ATTENDANCE', 'MANAGE_ATTENDANCE') ||
        isModuleFeatureAllowed('ATTENDANCE', 'MANAGE_ROSTER')
      )
    },
    allowEnableFetch() {
      if (
        this.$route.path.startsWith('/workforce/collection_management') ||
        this.$route.path.startsWith('/workforce/loan_management') ||
        this.$route.path.startsWith('/workforce/team-review') ||
        this.$route.path.startsWith('/workforce/base') ||
        this.$route.name === 'DynamicUseCase'
      ) {
        return true
      } else {
        return false
      }
    },
    showGlobalSearch() {
      const currentRoute =
        Object.values(routeConfig).find((route) => this.$route.path.startsWith(route.path)) ||
        (this.$route.path.startsWith('/workforce/')
          ? SearchConfig.getRouteConfig(this.$route.path)
          : null)

      // Check if route exists and is visible
      if (!currentRoute || currentRoute.visibility !== true) {
        return false
      }

      // Use the same logic as SearchConfig.getSelectItems to determine available selection items
      let selectValues = currentRoute.selectValues || []

      // If activeTabFilter is defined, filter selectValues based on active tab
      if (currentRoute.activeTabFilter) {
        const activeTab = this.$route.query.activeTab || 'Default'
        const filterMap = currentRoute.activeTabFilter
        selectValues = filterMap[activeTab] || filterMap.Default || selectValues
      }

      // Show global search only if there are selection items available
      return selectValues.length > 0
    },
    shouldShowUpgradeButton() {
      return (this.daysLeft !== null && this.daysLeft < 10) || this.lastPaymentAmount === 0
    },
    currentYear() {
      return new Date().getFullYear()
    }
  },
  beforeDestroy() {
    // Clear the interval to avoid memory leaks
    clearInterval(this.helpTextInterval)
    document.removeEventListener('click', this.handleOutsideClick)
    this.$root.$off('open-tutorial', this.openTutorialDialog)
    this.$root.$off('open-guide', this.openGuideDialog)
  },
  async mounted() {
    await this.initializeLayout()

    document.addEventListener('click', this.handleOutsideClick)
    this.$root.$on('open-tutorial', this.openTutorialDialog)
    this.$root.$on('open-guide', this.openGuideDialog)
  },
  methods: {
    openGuideDialog() {
      this.guideDialog = true
    },
    openTutorialDialog() {
      this.$refs.tutorialCarouselDialog?.open()
    },
    // New Method for Plan Selection
    async handlePlanSelection(plan) {
      this.loadingPlan = plan.name

      if (!this.$refs.razorpayCheckout) {
        console.error('Razorpay component ref not found')
        this.loadingPlan = null
        return
      }

      const checkoutPlan = {
        name: plan.name,
        // Price should be based on billing cycle
        price: this.billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice,
        id: plan.name.toUpperCase(),
        billingCycle: this.billingCycle,
        planRefId: plan._id
      }

      try {
        await this.$refs.razorpayCheckout.processPayment(checkoutPlan)
        // Close dialog on success? Or wait for verify?
        // processPayment handles flow, usually we can close if needed or let it be
      } catch (error) {
        console.error('Payment Flow Error:', error)
      } finally {
        this.loadingPlan = null
      }
    },
    goToDashboard() {
      if (this.$route.path !== '/workforce/dashboard') {
        this.$router.push('/workforce/dashboard').catch((err) => {
          if (err.name !== 'NavigationDuplicated') {
            console.error('Navigation error:', err)
          }
        })
      }
    },
    toggleOpenDrawer() {
      this.isRightDrawerOpen = !this.isRightDrawerOpen
    },
    handleOutsideClick(event) {
      // Close the RightDrawer when clicking outside
      if (this.isRightDrawerOpen) {
        const rightDrawerElement = this.$el.querySelector('.right-drawer-container')
        if (rightDrawerElement && !rightDrawerElement.contains(event.target)) {
          // Check if the click was on the toggle icon or button
          const toggleTrigger = this.$el.querySelector('.help-desk-trigger')
          const toggleIcon = this.$el.querySelector('.mdi-help-circle-outline')
          const isTriggerClick =
            (toggleTrigger && (event.target === toggleTrigger || toggleTrigger.contains(event.target))) ||
            (toggleIcon && (event.target === toggleIcon || toggleIcon.contains(event.target)))
          if (!isTriggerClick) {
            this.isRightDrawerOpen = false
          }
        }
      }
    },
    getRouteById(id) {
      const menuItem = this.menuItems.find((item) => item.id === id)
      return menuItem ? menuItem.to : null
    },
    toggleDarkMode() {
      this.isDarkMode = false
      this.applyTheme()
    },
    applyTheme() {
      this.isDarkMode = false
      document.documentElement.classList.remove('dark')
      if (this.$vuetify && this.$vuetify.theme) {
        this.$vuetify.theme.dark = false
      }
      localStorage.setItem('theme', 'light')
      this.$root.$emit('modeChange')
      this.isLoad = true
    },
    initFb() {
      window.fbAsyncInit = function () {
        if (window.FB) {
          window.FB.init({
            appId: '1622165388539821',
            cookie: true,
            xfbml: true,
            version: 'v19.0'
          })
          window.FB.AppEvents.logPageView()
        }
      }
        ; (function (d, s, id) {
          var js,
            fjs = d.getElementsByTagName(s)[0]
          if (d.getElementById(id)) {
            return
          }
          js = d.createElement(s)
          js.id = id
          js.src = 'https://connect.facebook.net/en_US/sdk.js'
          js.nonce = 'kyHuC4JA'
          fjs.parentNode.insertBefore(js, fjs)
        })(document, 'script', 'facebook-jssdk')
    },
    isPopselected(i) {
      console.log(i)
    },
    async getDynamicNavLinks() {
      // const config = await this.getConfig()
      const links = []

      // if (config.enabledSubCases?.length && isModuleVisible('COLLECTION_MANAGEMENT')) {
      //   config.enabledSubCases.forEach((subCaseItem, index) => {
      //     const subCasesLink = {
      //       id: `collection-management-${index}`, // Make ID unique
      //       icon: 'mdi-bank',
      //       title: 'Teams',
      //       visible: isModuleVisible('COLLECTION_MANAGEMENT'),
      //       childRoutes: []
      //     }

      //     if (this.userOrg?.branch && !this.userOrg?.role?.includes('WFM_ADMIN')) {
      //       const filteredSubCases = subCaseItem?.subCase?.filter(
      //         (sub) => sub?.branch === this.userOrg?.branch
      //       )
      //       filteredSubCases.forEach((subCase) => {
      //         subCasesLink.childRoutes.push({
      //           id: `subcase-${subCase.name}`,
      //           icon: 'mdi-cash-multiple',
      //           title: convertToTitleCase(subCase.name) || 'tet test',
      //           to: `/workforce/collection-management/${subCase._id}`
      //         })
      //       })
      //     } else {
      //       subCaseItem.subCase.forEach((subCase) => {
      //         subCasesLink.childRoutes.push({
      //           id: `subcase-${subCase.name}`,
      //           icon: 'mdi-cash-multiple',
      //           title: convertToTitleCase(subCase.name) || 'tet test',
      //           to: `/workforce/collection-management/${subCase._id}`
      //         })
      //       })
      //     }

      //     links.push(subCasesLink)
      //   })
      // }

      const modules = [
        // {
        //   key: 'base_management',
        //   icon: 'mdi-bag-checked',
        //   usecase: 'project',
        //   title: 'Project',
        //   permission: permissionData.projectRead
        // },
        {
          key: 'base_management',
          icon: 'mdi-shopping',
          usecase: 'inventory',
          title: 'Products',
          permission: permissionData.inventoryRead
        }
        // {
        //   key: 'base_management',
        //   icon: 'mdi-bag-checked',
        //   usecase: 'default',
        //   title: 'Lead',
        //   permission: permissionData.leadRead
        // }
      ]

      modules.forEach((module) => {
        if (module.key) {
          const moduleVisible =
            hasPermission(this.userData, module.permission) && isModuleVisible(module.key)
          links.push({
            id: `${module.key.toLowerCase()}-${module.title}`,
            icon: module.icon,
            title: module.title,
            to: `/workforce/leadspage/${module.usecase.toLowerCase()}`,
            visible: moduleVisible
          })
        }
      })

      return links
    },
    async fecthAllowedUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/allowed-use-cases`)
        this.allowedUseCases = res.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    // async getConfig() {
    //   try {
    //     const response = await this.$axios.get('/org/config')

    //     const data = response.data?.config
    //     this.useCases = data.enabledUseCases
    //     this.$store.dispatch('setSubCases', data.enabledSubCases)
    //     this.$store.dispatch('setUseCase', this.useCases)
    //     const obj = {
    //       directComplete: data.isTaskDirectCompletionAllowedOrg,
    //       recurAllow: data.isTaskRecurrenceAllowedOrg,
    //       isCheckedInSelfie: data.isCheckedInSelfie,
    //       isCheckedOutSelfie: data.isCheckedOutSelfie
    //     }
    //     const jsonString = JSON.stringify(obj)
    //     localStorage.setItem('task', jsonString)
    //     return data || {}
    //   } catch (error) {
    //     console.error('Error fetching config:', error)
    //   }
    // },
    toggleDropdown() {
      this.dropdownVisible = !this.dropdownVisible
    },
    handleSeeAll() {
      this.$router.push('/workforce/notifications?activeTab=notifications')
    },
    async logout() {
      try {
        await this.$axios.post('/auth/v3/logout')
      } catch (error) {
        console.log(error)
      }
      localStorage.clear()
      localStorage.setItem('theme', 'light')
      this.$storage?.removeUniversal('isAuthenticated')
      this.$storage?.removeUniversal('user')
      this.$store.dispatch('resetWfmPermissions')
      this.$store.dispatch('resetWfmStatements')
      this.$store.dispatch('resetSubCase')
      this.$router.replace('/login')
    },

    async setupMenuItems() {
      let isAnyItemVisible = false

      for (const item of this.menuItems) {
        if (item.visible) {
          isAnyItemVisible = true
        }

        if (!item.visible && this.$route.path === item.to) {
          if (this.$route.path !== '/workforce/dashboard') {
            this.$router.push('/workforce/dashboard').catch((err) => {
              if (err.name !== 'NavigationDuplicated') console.error(err)
            })
          }
        }
      }

      if (!isAnyItemVisible) {
        this.$toast.error("You don't have permission to access the admin portal")
        await this.logout()
        return
      }

      if (this.checkRouteVisibility) {
        const complainRoute = `${this.$route.path}?complaint_no=${this.$route.query?.complaint_no}`
        if (this.$route.fullPath !== complainRoute) {
          this.$router.push(complainRoute).catch((err) => {
            if (err.name !== 'NavigationDuplicated') console.error(err)
          })
        }
        return
      }
    },
    getCurrentDate() {
      const dateObj = new Date()
      const options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }
      return dateObj.toLocaleDateString('en-US', options)
    },
    isSelected(index) {
      const item = this.menuItems[index]

      if (item.to) {
        return this.$route.path.startsWith(item.to)
      }

      // Check if any child route is active for grouped items
      if (item.childRoutes && item.childRoutes.length > 0) {
        return item.childRoutes.some((child) => this.$route.path.startsWith(child.to))
      }

      return false
    },
    isItemSelected(item) {
      if (!item) return false
      if (item.to && this.$route.path.startsWith(item.to)) {
        return true
      }
      if (item.childRoutes && item.childRoutes.length > 0) {
        return item.childRoutes.some((child) => this.$route.path.startsWith(child.to))
      }
      return false
    },
    isChildActive(child) {
      if (!child || !child.to) return false
      return this.$route.path === child.to || this.$route.path.startsWith(child.to + '/')
    },
    getItemBadge(item) {
      if (!item) return null
      if (item.badge !== undefined && item.badge !== null && item.badge !== '') {
        return item.badge
      }
      if (item.badgeCount !== undefined && item.badgeCount !== null) {
        return item.badgeCount
      }
      if (item.title === 'Notifications' && this.alertsCount) {
        return this.alertsCount
      }
      return null
    },
    getItemCategory(item) {
      if (!item) return 'WORKSPACE'
      const id = (item.id || '').toLowerCase()
      const title = (item.title || '').toLowerCase()
      const to = (item.to || '').toLowerCase()

      // WORKSPACE
      if (
        id === 'dashboard' ||
        title === 'dashboard' ||
        id.startsWith('usecase-') ||
        to.includes('collection') ||
        to.includes('leadspage') ||
        title === 'collections' ||
        id === 'customers' ||
        title === 'customers' ||
        title === 'teams' ||
        title === 'attendance' ||
        to.includes('team-review')
      ) {
        return 'WORKSPACE'
      }

      // FINANCE
      if (
        id === 'accounts' ||
        title === 'accounts' ||
        id === 'invoices' ||
        title === 'invoices' ||
        id === 'approvals' ||
        title === 'approvals' ||
        id === 'product-allotment' ||
        title === 'inventory' ||
        id === 'inventory-management' ||
        title === 'part management' ||
        id === 'inventory-settings' ||
        title === 'part settings' ||
        id === 'inventory-partners' ||
        title === 'lenders' ||
        id === 'amc-management' ||
        title === 'amc'
      ) {
        return 'FINANCE'
      }

      // INSIGHTS
      if (
        id === 'report-management' ||
        title === 'reports' ||
        id === 'batch-report-management' ||
        title === 'downloads' ||
        title === 'exports' ||
        id === 'voice-campaigns' ||
        id === 'campaigns' ||
        title === 'campaigns' ||
        title === 'dynamic forms' ||
        id === 'dynamic-forms' ||
        to.includes('dynamic-responses')
      ) {
        return 'INSIGHTS'
      }

      // ADMIN
      if (
        id === 'user' ||
        title === 'organization' ||
        to.includes('organization') ||
        id === 'org-settings' ||
        title === 'settings' ||
        to.includes('settings') ||
        id === 'workflow-steps' ||
        title === 'workflows' ||
        id === 'notification' ||
        title === 'notifications' ||
        id === 'learning' ||
        title === 'learning' ||
        to.includes('corporate-expenses')
      ) {
        return 'ADMIN'
      }

      return 'WORKSPACE'
    },
    getItemSortWeight(item) {
      if (!item) return 999
      const title = (item.title || '').toLowerCase()
      const id = (item.id || '').toLowerCase()

      const weights = {
        // WORKSPACE
        dashboard: 10,
        collections: 20,
        customers: 30,
        teams: 40,
        attendance: 50,

        // FINANCE
        accounts: 110,
        invoices: 120,
        approvals: 130,
        inventory: 140,
        'part management': 150,
        'part settings': 160,
        amc: 170,
        lenders: 180,

        // INSIGHTS
        reports: 210,
        downloads: 220,
        exports: 220,
        campaigns: 230,
        'dynamic forms': 240,

        // ADMIN
        organization: 310,
        settings: 320,
        workflows: 330,
        notifications: 340,
        learning: 350
      }

      if (id.startsWith('usecase-')) return 20 // Collections / dynamic cases
      return weights[title] || weights[id] || 999
    },
    isPopupItemSelected(popupItem) {
      return this.$route.path === popupItem.to || this.$route.path.startsWith(popupItem.to + '/')
    },
    getEmployeeImage() {
      return `${this.baseUrlS3 + this.user?.user.profile_image}`
    },
    openInNewTab() {
      window.open('https://www.cualitywork.com/', '_blank')
    },
    loadExternalScripts() {
      const pusherScript = document.createElement('script')
      pusherScript.src = PUSHER_CLIENT_LIB
      pusherScript.async = true
      pusherScript.onload = () => {
        this.initPusher()
      }
      document.head.appendChild(pusherScript)
    },

    initPusher() {
      const userId = this.userOrg?._id

      // Request browser notification permission if not asked yet
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission()
      }

      Pusher.logToConsole = false
      const pusher = new Pusher(process.env.VUE_APP_PUSHER_ID, {
        cluster: process.env.VUE_APP_PUSHER_CLUSTER
      })
      const channel = pusher.subscribe(`${NOTIFICATION_CHANNEL}-${userId}`)
      channel.bind(NOTIFICATION_CHANNEL, (data) => {
        this.alertsCount = data?.pagination.totalCount
        this.alerts = [...reformatNotification(data?.notifications)]

        // Play chime sound
        this.playNotificationChime()

        // Show native browser desktop notification
        const latest = data?.notifications?.[0]
        if (latest) {
          this.showDesktopNotification('New Notification', latest.notificationMessage || '')
        }
      })
    },

    playNotificationChime() {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext
        if (!AudioContext) return
        const ctx = new AudioContext()

        // Tone 1
        const osc1 = ctx.createOscillator()
        const gain1 = ctx.createGain()
        osc1.type = 'sine'
        osc1.frequency.setValueAtTime(880, ctx.currentTime) // A5
        gain1.gain.setValueAtTime(0.15, ctx.currentTime)
        gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15)
        osc1.connect(gain1)
        gain1.connect(ctx.destination)
        osc1.start(ctx.currentTime)
        osc1.stop(ctx.currentTime + 0.15)

        // Tone 2 (delayed, higher pitch)
        const osc2 = ctx.createOscillator()
        const gain2 = ctx.createGain()
        osc2.type = 'sine'
        osc2.frequency.setValueAtTime(1046.5, ctx.currentTime + 0.08) // C6
        gain2.gain.setValueAtTime(0.15, ctx.currentTime + 0.08)
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3)
        osc2.connect(gain2)
        gain2.connect(ctx.destination)
        osc2.start(ctx.currentTime + 0.08)
        osc2.stop(ctx.currentTime + 0.3)
      } catch (e) {
        console.error('Failed to play notification chime:', e)
      }
    },

    showDesktopNotification(title, body) {
      if (!('Notification' in window)) return

      const options = {
        body: body,
        icon: this.orgData?.logo
          ? `${process.env.VUE_APP_S3_BASE_URL + this.orgData?.logo}`
          : '/favicon.ico'
      }

      if (Notification.permission === 'granted') {
        new Notification(title, options)
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then((permission) => {
          if (permission === 'granted') {
            new Notification(title, options)
          }
        })
      }
    },

    async fetchNotifications() {
      try {
        const response = await this.$axios.get('/notifications')
        this.alertsCount = response.data?.pagination?.totalCount
        this.alerts = [...reformatNotification(response.data?.notifications)]
      } catch (error) {
        console.error('Error fetching notifications:', error)
      }
    },
    categoryColor(generic) {
      const colorInfo = notificationColor[generic]
      return colorInfo
    },
    async updateOrg(url) {
      const payload = {
        orgCode: this.orgCode,
        name: this.orgData?.name,
        logo: url.doc_physical_path
      }
      try {
        const response = await this.$axios.put('/org/info', payload)

        if (response.data.success) {
          return true
        } else {
          return false
        }
      } catch (error) {
        console.error('Error updating org.', error)
      }
    },
    // async fetchChecklist(id) {
    //   try {
    //     if (!id) return
    //     const response = await this.$axios.get(`/orgSetup/org-onboarding-checklist/${id}`)
    //     console.log(Object.values(response.data?.checklist))
    //     const isAnyChecklistInComplete = Object.values(response.data?.checklist).some(
    //       (item) => item.completed === false
    //     )
    //     console.log(isAnyChecklistInComplete)
    //     if (isAnyChecklistInComplete) {
    //       localStorage.setItem('needsInitialSetup', 'true')
    //     }
    //   } catch (error) {
    //     console.log(error)
    //   }
    // },
    async getOrgData() {
      try {
        const response = await this.$axios.get('/organizations')
        this.orgData = response.data?.organizations[0]
        if (this.orgData.plan_status && this.orgData.plan_details) {
          this.planStatus = this.orgData.plan_status === 'active' ? false : true
        }

        localStorage.setItem('orgData', JSON.stringify(this.orgData))
        localStorage.setItem('organization_code', this.orgData?.code)
        // await this.fetchChecklist(this.orgData?.id)
      } catch (error) {
        console.error('Error getting organization data.', error)
      }
    },
    openDialerCard() {
      this.isDialerOpen = !this.isDialerOpen
    },
    openComplaintCard() {
      this.isComplaintOpen = !this.isComplaintOpen
      if (this.isComplaintOpen) {
        this.isRightDrawerOpen = false
        this.isHelpTextVisible = false
        clearInterval(this.helpTextInterval)
      } else {
        this.startHelpTextLoop()
      }
    },
    startHelpTextLoop() {
      this.helpTextInterval = setInterval(() => {
        this.isHelpTextVisible = true
        setTimeout(() => {
          this.isHelpTextVisible = false
        }, 6000)
      }, 10000)
    },
    downloadApp() {
      const appLink = process.env.VUE_APP_EMP_APP_LINK
      window.open(appLink, '_blank')
    },
    isCallingAllowed(moduleName) {
      return isModuleFeatureAllowed('ADDONS', moduleName)
    },
    isbellIconEnabled(moduleName) {
      return (
        hasPermission(this.userData, permissionData.notificationRead) && isModuleVisible(moduleName)
      )
    },
    toggleOptionsDrawer() {
      this.isOptionsDrawerOpen = !this.isOptionsDrawerOpen
    },
    async fetchEmployees() {
      try {
        const chain = JSON.parse(localStorage.getItem('userReporteeChain'))
        if (chain) {
          return
        }

        const allUser = {
          limit: '*',
          sortBy: 'firstName',
          sortOrder: 'asc'
        }
        const response = await this.$axios.get(axiosUrl.employees.getEmployees, {
          params: allUser
        })
        if (response.data.success) {
          const userTree = this.constructReporteeTree(response.data.users)
          this.$storage.setUniversal('userReporteeChain', userTree)
          // Keep localStorage for backward compatibility if needed, though $storage usually handles it
          localStorage.setItem('userReporteeChain', JSON.stringify(userTree))
        }
      } catch (error) {
        console.error('Error fetching employees:', error)
      }
    },
    constructReporteeTree(data) {
      const map = {}
      data.forEach((employee) => {
        map[employee._id] = { ...employee }
      })

      const buildReportesToChain = (employee, visited = new Set()) => {
        if (!employee.reportesTo || !employee.reportesTo._id) return null

        const managerId = employee.reportesTo._id
        if (visited.has(managerId)) return null
        visited.add(managerId)

        const manager = map[managerId]

        if (!manager) return null

        return {
          firstName: manager.firstName,
          lastName: manager.lastName,
          fullName: manager.fullName,
          reportesTo: buildReportesToChain(manager, visited),
          _id: manager._id
        }
      }

      const buildManagerChain = (employee) => {
        const chain = []
        let current = employee
        const visited = new Set()

        while (current.reportesTo && current.reportesTo._id) {
          const managerId = current.reportesTo._id

          if (visited.has(managerId)) break
          visited.add(managerId)

          const manager = map[managerId]
          if (!manager) break
          chain.push(manager.fullName)
          current = manager
        }

        return chain.join(' -> ')
      }

      const tree = {}
      data.forEach((employee) => {
        tree[employee._id] = {
          firstName: employee.firstName,
          lastName: employee.lastName,
          fullName: employee.fullName,
          reportesTo: buildReportesToChain(employee),
          managerChain: buildManagerChain(employee),
          _id: employee._id
        }
      })
      return tree
    },
    async fetchAllUseCases() {
      try {
        const res = await this.$axios.get(`/orgSetup/extend-usecase`)
        this.allSubCases = res.data?.data
      } catch (error) {
        console.log(error)
      }
    },
    extractAllowedUseCaseName(key) {
      const allowedUseCase = this.allowedUseCases.find((u) => u.useCase === key)
      return { name: allowedUseCase?.navName, icon: allowedUseCase?.navIcon }
    },
    getUseCaseMenuItems() {
      if (!this.allowedUseCases || !this.allowedUseCases.length) return []

      return this.allowedUseCases
        .map((allowedUseCase) => {
          const useCase = allowedUseCase.useCase
          // Filter subcases that belong to this allowed use case
          const subCases = (this.allSubCases || []).filter((sc) => sc.useCase === useCase)

          if (subCases.length === 0) return null

          return {
            id: `usecase-${useCase}`,
            icon: allowedUseCase.navIcon || 'mdi-briefcase-variant-outline',
            // If navName is missing, use the name of the first subCase as the group title
            title: allowedUseCase.navName || convertToTitleCase(subCases[0].name),
            visible: true,
            childRoutes: subCases.map((subCase) => ({
              id: `usecase-${useCase}-${subCase._id}`,
              title: convertToTitleCase(subCase.name),
              icon: subCase?.icon || 'mdi-file-document-outline',
              to: `/workforce/${useCase}/${subCase._id}`
            }))
          }
        })
        .filter((item) => item !== null)
    },
    showPopup(index, event) {
      const item = this.menuItems[index]
      if (this.hidePopupTimeout) {
        clearTimeout(this.hidePopupTimeout)
        this.hidePopupTimeout = null
      }
      if (this.currentHoveredItem === index && this.showItemPopup) {
        return
      }
      if (item && item.childRoutes && item.childRoutes.length > 0) {
        this.currentHoveredItem = index
        this.popupItems = item.childRoutes

        // Position the popup to the right of the hovered item
        const rect = event.currentTarget.getBoundingClientRect()
        this.popupPosition = {
          top: rect.top + window.scrollY,
          left: rect.right + 10
        }

        this.showItemPopup = true
      } else {
        this.scheduleHidePopup()
      }
    },

    scheduleHidePopup() {
      if (this.hidePopupTimeout) {
        clearTimeout(this.hidePopupTimeout)
      }
      this.hidePopupTimeout = setTimeout(() => {
        this.showItemPopup = false
        this.currentHoveredItem = null
        this.hidePopupTimeout = null
      }, 300)
    },

    cancelHidePopup() {
      if (this.hidePopupTimeout) {
        clearTimeout(this.hidePopupTimeout)
        this.hidePopupTimeout = null
      }
    },

    navigateTo(path) {
      try {
        if (this.$route.path !== path) {
          this.$router.push(path)
        }
      } catch (error) {
        if (error.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', error)
        }
      }
      this.showItemPopup = false
      this.currentHoveredItem = null
    },
    async initializeLayout() {
      try {
        const res = await this.$axios.get('/cualitywork/auth-user')
        if (res.data && res.data.wfmPermissions) {
          this.$store.dispatch('setWfmPermissions', res.data.wfmPermissions)
          this.$store.dispatch('setWfmStatements', res.data.wfmStatements || [])
        }
      } catch (err) {
        console.error('Error refreshing WFM permissions:', err)
      }
      await this.startHelpTextLoop()
      this.isDarkMode = false
      this.applyTheme()
      await this.getOrgData()
      this.loadExternalScripts()
      this.fetchNotifications()
      await this.fecthAllowedUseCases()
      // this.initFb()
      const _this = this
      setInterval(function () {
        const user = _this.$storage.getUniversal('user')
        if (user) {
          _this.user_name = user?.first_name
        }
        if (user?.role[0] === 'SUPER_ADMIN') {
          _this.title = 'Super Admin Panel'
        }
      }, 100)
      const userDataFromStorage = this.$storage.getUniversal('user')
      this.user = userDataFromStorage
      const scope = userDataFromStorage?.scope
      const dynamicNavLinks = await this.getDynamicNavLinks()
      // console.log(dynamicNavLinks)
      await this.fetchAllUseCases() // Make sure to fetch use cases before creating menu items

      this.menuItems = [
        {
          id: 'dashboard',
          icon: 'mdi-view-grid-outline',
          title: 'Dashboard',
          to: '/workforce/dashboard',
          visible:
            hasPermission(this.userData, permissionData.userRead) && isModuleVisible('dashboard')
        },
        {
          id: 'user',
          icon: 'mdi-sitemap-outline',
          title: 'Organization',
          to: '/workforce/organization',
          visible:
            hasPermission(this.userData, permissionData.userRead) &&
            isModuleVisible('employee_management')
        },
        {
          id: 'organization',
          icon: 'mdi-clock-outline',
          title: 'Attendance',
          to: '/workforce/team-review',
          visible:
            hasPermission(this.userData, permissionData.attendenceRead) &&
            isModuleVisible('attendance_management') &&
            this.isEveryTabsInModule
        },
        ...dynamicNavLinks,
        ...this.getUseCaseMenuItems(),
        {
          id: 'customers',
          icon: 'mdi-account-multiple-outline',
          title: 'Customers',
          to: '/workforce/product-customers',
          visible:
            hasPermission(this.userData, permissionData.customerRead) &&
            !isModuleVisible('collection_management') &&
            isModuleVisible('customers') &&
            this.orgCode === 'MITTAL'
        },
        {
          id: 'customers',
          icon: 'mdi-account-multiple-outline',
          title: 'Customers',
          to: '/workforce/customers',
          visible:
            hasPermission(this.userData, permissionData.customerRead) &&
            (isModuleVisible('collection_management') || isModuleVisible('loan_management')) &&
            isModuleVisible('customers')
        },
        {
          id: 'accounts',
          icon: 'mdi-bank-outline',
          title: 'Accounts',
          to: '/workforce/accounts',
          visible: true
        },
        {
          id: 'invoices',
          icon: 'mdi-receipt-text-outline',
          title: 'Invoices',
          to: '/workforce/invoices',
          visible: true
        },
        {
          id: 'inventory-partners',
          icon: 'mdi-handshake-outline',
          title: 'Lenders',
          to: '/workforce/partners',
          visible: false
        },
        {
          id: 'workflow-steps',
          icon: 'mdi-source-branch',
          title: 'Workflows',
          to: '/workforce/workflow-steps',
          visible: isModuleVisible('loan_management')
        },
        // {
        //   id: 'campaigns',
        //   icon: 'mdi-bullhorn-outline',
        //   title: 'Campaigns',
        //   to: '/workforce/campaigns',
        //   visible: this.isCallingAllowed('CALL_A_CUSTOMER')
        // },
        // inventory_management
        {
          id: 'product-allotment',
          icon: 'mdi-cube-outline',
          title: 'Inventory',
          to: '/workforce/product-inventory',
          visible: true
          // visible: isModuleVisible('inventory_management')
        },
        {
          id: 'inventory-management',
          icon: 'mdi-package-variant-closed',
          title: 'Part Management',
          to: '/workforce/stock-management',
          visible: this.sparePartEnabled
        },
        {
          id: 'inventory-settings',
          icon: 'mdi-cog-box',
          title: 'Part Settings',
          to: '/workforce/inventory-settings',
          visible: this.sparePartEnabled
        },
        {
          id: 'amc-management',
          icon: 'mdi-shield-check-outline',
          title: 'AMC',
          to: '/workforce/amc',
          visible: this.amcEnabled
        },
        {
          id: 'report-management',
          icon: 'mdi-chart-bar',
          title: 'Reports',
          to: '/workforce/reports',
          visible: isModuleVisible('reports')
        },
        {
          id: 'batch-report-management',
          icon: 'mdi-tray-arrow-down',
          title: 'Downloads',
          to: '/workforce/batch-reports',
          visible: isModuleVisible('reports')
        },
        {
          id: 'dynamic-forms',
          icon: 'mdi-form-select',
          title: 'Dynamic Forms',
          to: '/workforce/dynamic-responses',
          visible: true
        },
        {
          id: 'notification',
          icon: 'mdi-bell-outline',
          title: 'Notifications',
          to: '/workforce/notifications',
          visible:
            hasPermission(this.userData, permissionData.notificationRead) &&
            isModuleVisible('notifications')
        },
        {
          id: 'voice-campaigns',
          icon: 'mdi-bullhorn-outline',
          title: 'Campaigns',
          to: '/workforce/voice-campaigns',
          visible: true
        },
        {
          id: 'approvals',
          icon: 'mdi-checkbox-marked-circle-outline',
          title: 'Approvals',
          to: '/workforce/approvals',
          visible: true
        },
        {
          id: 'learning',
          icon: 'mdi-school-outline',
          title: 'Learning',
          to: '/workforce/learning',
          visible: this.orgCode === 'KWALTY'
        },
        // {
        //   id: 'pdf-templates',
        //   icon: 'mdi-file-pdf-box',
        //   title: 'PDF Templates',
        //   to: '/workforce/pdf-templates',
        //   visible: true
        // },
        {
          id: 'org-settings',
          icon: 'mdi-cog-outline',
          title: 'Settings',
          to: '/workforce/settings',
          visible:
            hasPermission(this.userData, permissionData.settingRead) && isModuleVisible('settings')
        }
        // {
        //   id: 'corporate-expenses',
        //   icon: 'mdi-cash-multiple',
        //   title: 'Corporate Expenses',
        //   to: '/workforce/corporate-expenses',
        //   visible:
        //     scope.indexOf('ADMIN_ACCESS') > -1 &&
        //     userData.role.indexOf('ORG_ADMIN') > -1 &&
        //     isModuleVisible('CLAIMS')
        // }
      ]

      await this.setupMenuItems()
      await this.fetchEmployees()

      // Fetch payment history after orgData is available
      if (this.orgData?.id) {
        await this.fetchPaymentHistory()
      }
    },
    async fetchPaymentHistory() {
      try {
        const orgId = this.orgData?.id
        const res = await this.$axios.get(`/orgSetup/payment-history/${orgId}`)
        const payments = res.data?.data || res.data
        const history = Array.isArray(payments) ? payments : [payments]

        if (history.length > 0) {
          const lastPayment = history[0]

          if (lastPayment) {
            const created = new Date(lastPayment.createdAt)
            const expiry = new Date(created)

            if (lastPayment.paymentType === 'MONTHLY') {
              expiry.setDate(expiry.getDate() + 30)
            } else if (lastPayment.paymentType === 'YEARLY') {
              expiry.setDate(expiry.getDate() + 365)
            }

            const now = new Date()
            const diffTime = expiry - now
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
            this.daysLeft = diffDays
            this.lastPaymentAmount = lastPayment.amount
            // this.planName = lastPayment.planId
          }
        }
      } catch (error) {
        console.error('Error fetching payment history', error)
      }
    },
    async setUser() {
      try {
        const res = await this.$axios.get('/cualitywork/auth-user')
        this.$storage.setUniversal('user', { ...res.data })
        this.$store.dispatch('setWfmPermissions', res.data.wfmPermissions)
        this.$store.dispatch('setWfmStatements', res.data.wfmStatements || [])

        window.location.reload()
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>

<style>
header>div.v-toolbar__content {
  width: 100%;
  padding-left: 0px !important;
}

.user-menu>button {
  width: 100% !important;
}

.back {
  background-color: #f3f7ff;
}

.selected-menu {
  background: #21427d;
}

.dark .selected-menu {
  background-color: #4a95da;
}

/* Child route selected - lighter than parent */
.child-selected {
  background: #5581d4;
}

.dark .child-selected {
  background-color: #5581d4;
}

.selected-menu .text-blue-950 {
  color: white;
}

.selected-menu .white {
  color: white;
}

.justify-start .ssr-carousel-track {
  justify-content: flex-start !important;
}

.scroll-bar::-webkit-scrollbar {
  display: none;
}

.custom-btn {
  border-radius: 0px;
}

/* Custom Sidebar Styles */
.wfm-navbar {
  max-width: 300px;
  background-color: #0B2353 !important;
  border-right: 1px solid #153368 !important;
}

.wfm-top-navbar {
  background-color: #0B2353 !important;
  border-bottom: 1px solid #153368 !important;
  z-index: 25 !important;
}

.wfm-bottom-navbar {
  background-color: #f1f5f9 !important;
  border-top: 1px solid #e2e8f0 !important;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.05) !important;
  z-index: 30;
  display: flex !important;
  align-items: center !important;
}

.wfm-footer-content {
  width: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  height: 100% !important;
}

.wfm-footer-copyright {
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.01em;
  white-space: nowrap;
  user-select: none;
}

.bottom-nav-btn {
  background-color: #ffffff !important;
  color: #0B2353 !important;
  border: 1px solid #cbd5e1 !important;
  font-weight: 600 !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
  transition: all 0.2s ease !important;
}

.bottom-nav-btn:hover {
  background-color: #f8fafc !important;
  border-color: #94a3b8 !important;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
}

.bottom-nav-btn .v-icon {
  color: #0B2353 !important;
}

.dark .wfm-footer-copyright {
  color: #94a3b8;
}

.sidebar-category-header {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #728fb5;
  text-transform: uppercase;
  padding: 14px 18px 6px 18px;
  user-select: none;
}

.sidebar-category-group:first-child .sidebar-category-header {
  padding-top: 6px;
}

/* Sidebar Nav Item (Capsule Pill) */
.sidebar-nav-item {
  border-radius: 14px !important;
  margin: 3px 10px !important;
  min-height: 42px !important;
  padding: 0 14px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.sidebar-nav-item::before {
  border-radius: 14px !important;
  background-color: transparent !important;
}

/* Active Pill */
.sidebar-pill-active {
  background-color: #ffffff !important;
  color: #0B2353 !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
}

.sidebar-pill-active .v-icon {
  color: #0B2353 !important;
}

.sidebar-pill-active .sidebar-item-title {
  color: #0B2353 !important;
  font-weight: 700 !important;
}

/* Inactive Pill */
.sidebar-pill-inactive {
  background-color: transparent !important;
  color: #cbd7e8 !important;
}

.sidebar-pill-inactive .v-icon {
  color: #cbd7e8 !important;
  transition: color 0.2s ease;
}

.sidebar-pill-inactive .sidebar-item-title {
  color: #cbd7e8 !important;
  font-weight: 500;
  transition: color 0.2s ease;
}

.sidebar-pill-inactive:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.sidebar-pill-inactive:hover .v-icon,
.sidebar-pill-inactive:hover .sidebar-item-title {
  color: #ffffff !important;
}

/* Group Activator */
.sidebar-nav-group>.v-list-group__header {
  border-radius: 14px !important;
  margin: 3px 10px !important;
  min-height: 42px !important;
  padding: 0 14px !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar-nav-group>.v-list-group__header::before {
  border-radius: 14px !important;
  background-color: transparent !important;
}

.sidebar-nav-group.group-active>.v-list-group__header {
  background-color: #ffffff !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
}

.sidebar-nav-group.group-active>.v-list-group__header .v-icon,
.sidebar-nav-group.group-active>.v-list-group__header .sidebar-item-title,
.sidebar-nav-group.group-active>.v-list-group__header .v-list-group__header__append-icon .v-icon {
  color: #0B2353 !important;
  font-weight: 700 !important;
}

.sidebar-nav-group:not(.group-active)>.v-list-group__header {
  background-color: transparent !important;
  color: #cbd7e8 !important;
}

.sidebar-nav-group:not(.group-active)>.v-list-group__header .v-icon,
.sidebar-nav-group:not(.group-active)>.v-list-group__header .sidebar-item-title,
.sidebar-nav-group:not(.group-active)>.v-list-group__header .v-list-group__header__append-icon .v-icon {
  color: #cbd7e8 !important;
}

.sidebar-nav-group:not(.group-active)>.v-list-group__header:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
}

.sidebar-nav-group:not(.group-active)>.v-list-group__header:hover .v-icon,
.sidebar-nav-group:not(.group-active)>.v-list-group__header:hover .sidebar-item-title,
.sidebar-nav-group:not(.group-active)>.v-list-group__header:hover .v-list-group__header__append-icon .v-icon {
  color: #ffffff !important;
}

/* Child item styles */
.sidebar-child-item {
  border-radius: 10px !important;
  margin: 2px 10px 2px 22px !important;
  min-height: 36px !important;
  padding: 0 10px !important;
  transition: all 0.2s ease;
}

.sidebar-child-item::before {
  border-radius: 10px !important;
  background-color: transparent !important;
}

.sidebar-child-active {
  background-color: rgba(255, 255, 255, 0.16) !important;
  color: #ffffff !important;
}

.sidebar-child-active .v-icon {
  color: #ffffff !important;
}

.sidebar-child-inactive {
  background-color: transparent !important;
  color: #a4bedc !important;
}

.sidebar-child-inactive:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: #ffffff !important;
}

.sidebar-child-inactive:hover .v-icon {
  color: #ffffff !important;
}

/* Badge styles */
.sidebar-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  border-radius: 9999px;
  padding: 2px 8px;
  line-height: 1.2;
}

.sidebar-badge-active {
  background-color: #dbeafe !important;
  color: #1d4ed8 !important;
}

.sidebar-badge-inactive {
  background-color: rgba(255, 255, 255, 0.14) !important;
  color: #cbd7e8 !important;
}

/* Mini Variant Adjustments */
.v-navigation-drawer--mini-variant .sidebar-nav-item,
.v-navigation-drawer--mini-variant .sidebar-nav-group>.v-list-group__header {
  margin: 3px 6px !important;
  padding: 0 10px !important;
  min-height: 42px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  text-align: left !important;
}

.v-navigation-drawer--mini-variant .sidebar-item-row {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  text-align: left !important;
  width: 100% !important;
}

.v-navigation-drawer--mini-variant .sidebar-item-icon {
  margin-right: 8px !important;
}

.v-navigation-drawer--mini-variant .sidebar-item-title {
  display: inline-block !important;
  font-size: 12px !important;
  text-align: left !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 66px !important;
}

.v-navigation-drawer--mini-variant .v-list-group__header__append-icon {
  display: none !important;
}

/* Brand logo styling */
.sidebar-brand-wrapper {
  background: transparent;
}

.sidebar-logo {
  max-height: 38px;
  max-width: 160px;
  object-fit: contain;
}

.sidebar-divider {
  height: 1px;
  background-color: #153368;
  margin: 4px 12px 6px 12px;
}

/* Dark mode theme overrides */
.dark .wfm-navbar {
  background-color: #0B2353 !important;
}

.dark .wfm-top-navbar {
  background-color: #0B2353 !important;
}

.dark .wfm-bottom-navbar {
  background-color: #1e293b !important;
  border-top: 1px solid #334155 !important;
}

.dark .bottom-nav-btn {
  background-color: #0f172a !important;
  color: #f8fafc !important;
  border: 1px solid #334155 !important;
}

.dark .bottom-nav-btn .v-icon {
  color: #60a5fa !important;
}

/* .wfm-navbar .v-icon {
    font-size: 20px !important;
  } */

.image-container {
  position: relative;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* .edit-btn {
  position: absolute !important;
  top: -8px;
  right: 0px;
  z-index: 10;
  padding: 2px;
  background-color: #ffffff;
  border-radius: 50%;
  border: 1px solid #2a83ff;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
} */
/* .logo-icon {
  position: absolute !important;
  top: 10px;
  right: 18px;
  z-index: 2;
} */

/* .edit-btn:hover {
  background: #21427d;
  color: #fff;
}

.edit-btn::before {
  display: flex;
  align-items: center;
  justify-content: center;
} */

.colorText {
  color: #21427d;
}

.no-animation.v-btn--icon.v-size--default {
  width: auto;
  height: auto;
}

.no-animation.theme--light.v-btn:hover::before {
  opacity: 0;
}

.no-animation.theme--light.v-btn:hover::after {
  opacity: 0;
}

.fixed-bottom-btn {
  position: fixed;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  font-size: 10px !important;
}

.scrollable-list {
  max-height: calc(100vh - 80px);
  overflow-y: auto;
  overflow-x: hidden;
}

.arrow {
  width: 8px;
  height: 8px;
  position: absolute;
  z-index: -1;
}

.arrow::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #000;
  transform: rotate(-45deg);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.help-text-container {
  background-color: #f0f0f0;
  padding: 5px 10px;
  border-radius: 10px;
  cursor: pointer;
  max-width: 250px;
}

.help-text {
  font-size: 14px;
  color: #21427d;
  font-weight: bold;
  text-align: center;
}

.options-drawer {
  width: 250px;
  background-color: #f7f7f7;
}

.dialer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dialer-container {
  background: transparent;
  max-width: 90%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.notification {
  z-index: 10;
}

.v-dialog>.v-card>.v-card__text {
  padding: 0px !important;
}

.popup-container {
  position: absolute;
  z-index: 1000;
  background: #0B2353;
  border: 1px solid #1b3d7a;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5);
  min-width: 220px;
  max-height: 420px;
  overflow: hidden;
  transition: all 0.2s ease-in-out;
}

/* Added left side direction triangle */
.popup-container::before {
  content: '';
  position: absolute;
  top: 16px;
  left: -8px;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid #0B2353;
  z-index: 1001;
}

.popup-header {
  padding: 12px 16px;
  border-bottom: 1px solid #153368;
  background-color: #081a3e;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
}

.popup-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  color: #ffffff;
  text-align: start;
}

.popup-content {
  max-height: 320px;
  overflow-y: auto;
  padding: 6px 8px;
}

.popup-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 2px;
  transition: all 0.15s ease;
  color: #cbd7e8;
}

.popup-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.popup-item-selected {
  background-color: #ffffff !important;
  color: #0B2353 !important;
}

.popup-item-selected .v-icon {
  color: #0B2353 !important;
}

.popup-item-selected span {
  color: #0B2353 !important;
  font-weight: 700;
}

.scrollable-popup::-webkit-scrollbar {
  width: 5px;
}

.scrollable-popup::-webkit-scrollbar-track {
  background: transparent;
}

.scrollable-popup::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.scrollable-popup::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.35);
}

.dark .popup-container {
  background: #0B2353;
  color: white;
}

.dark .popup-header {
  background-color: #081a3e;
  border-bottom: 1px solid #153368;
}

.dark .popup-title {
  color: white;
}

.dark .popup-item:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.dark .popup-item-selected {
  background-color: #ffffff;
  color: #0B2353;
}

.dark .popup-item-selected .v-icon {
  color: #0B2353 !important;
}

/* Added styles for dark mode triangle */
.dark .popup-container::before {
  border-right-color: #0B2353;
}

.mini-variant-text {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 92px;
  text-align: center;
  font-size: 11px !important;
  margin-top: 2px;
}

.v-dialog>.v-card>.v-card__title {
  padding: 0px !important;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 0px !important;
  color: #21427d;
  margin: 0;
}

.logo-preview-container {
  width: 500px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.img-size {
  /* max-width: 40px; */
  height: auto;
}

.download-image {
  height: 34px !important;
  max-height: 34px !important;
  width: auto !important;
  object-fit: contain !important;
  display: block !important;
  transition: transform 0.2s ease;
}

.download-image:hover {
  transform: scale(1.04);
}

.top-bar {
  padding: 0px !important;
}
</style>
